import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography, SplitFormLayout, IconButton, Menu, MenuItem, MenuIcon, LogoutIcon, Drawer, CloseIcon, Dialog } from '@/ui';
import { useNavigate } from 'react-router-dom';
import Screen from '@/engine/renderer/Screen';
import { useForm, useUI } from '@/shared/state/store';
import { useAlert } from '@/app/providers/AlertProvider';
import { OnboardingService } from '@/shared/api/services/onboardingService';
import { useAuth } from '@/shared/state/store';
import { useHelp } from '@/shared/help/HelpProvider';
import Footer from '@/components/on-boarding/Footer';
import { useDOMErrorSuppression } from '@/shared/hooks/useDOMErrorSuppression';
// Use slide-up bottom sheet (Drawer) for error presentation to match Auth screen

// Import Schema type from Screen component to avoid type conflicts
type ScreenSchema = Parameters<typeof Screen>[0]['schema'];

// Address verification schema (formerly onboarding step 1)
import postCode from '@/engine/schema/custom/post-code.json';

import SimpleLoadingScreen from '@/components/SimpleLoadingScreen';

const carouselImages = [
  '/assets/onBoarding/crausal1.webp',
  '/assets/onBoarding/crausal2.webp',
  '/assets/onBoarding/crausal3.webp',
  '/assets/onBoarding/crausal4.webp'
];

export default function AddressVerification(): JSX.Element {
  useDOMErrorSuppression(true);
  const navigate = useNavigate();
  const { setFormData, formData: allFormData } = useForm();
  const { setLoading, loading } = useUI();
  const { showAlert } = useAlert();
  const { logout, user } = useAuth();
  const { openFaqForSchema } = useHelp();

  // Mobile header/footer sizing
  const headerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(120);
  const [footerHeight, setFooterHeight] = useState<number>(88);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);
  const [logoutMenuAnchor, setLogoutMenuAnchor] = useState<null | HTMLElement>(null);

  const [isVerifying, setIsVerifying] = useState(false);
  // Backend-driven block state (no frontend attempt counting)
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [lockedMessage, setLockedMessage] = useState<string | null>(null);
  const [errorSheetOpen, setErrorSheetOpen] = useState<boolean>(false);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);

  const isDefiniteLockedString = (text: string): boolean => {
    if (typeof text !== 'string' || text.length === 0) return false;
    const s = text.toLowerCase();
    // Match definite locked states, avoid future/conditional phrasing like "will be locked"
    return (
      s.includes('has been locked') ||
      s.includes('is locked') ||
      s.includes('was locked') ||
      s.includes('now locked') ||
      s.includes('account locked') ||
      s.includes('locked due to')
    );
  };

  const extractLockedMessage = (payload: unknown): string | null => {
    if (!payload || typeof payload !== 'object') return null;
    const data = payload as Record<string, unknown>;
    const title = typeof data.errorTitle === 'string' ? data.errorTitle : undefined;
    const errMsg = typeof data.errorMessage === 'string' ? data.errorMessage : undefined;
    const msg = typeof data.message === 'string' ? data.message : undefined;
    const code = typeof data.code === 'string' ? data.code : undefined;
    const errorCode = typeof data.errorCode === 'string' ? data.errorCode : undefined;
    const combined = [title, errMsg, msg, code, errorCode]
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
      .join(' ')
      .toLowerCase();
    // Show locked dialog only for definite locked phrasing
    if (typeof errMsg === 'string' && isDefiniteLockedString(errMsg)) {
      return errMsg;
    }
    // Handle generic payloads but only for definite locked phrasing
    if (isDefiniteLockedString(combined)) {
      return errMsg || msg || 'Your account has been locked due to multiple incorrect postcode attempts. Email customer.support@iehub.co.uk to unlock.';
    }
    // Spring WebFlux ResponseStatusException envelope with separate title + message
    if (typeof title === 'string' && title.includes('ResponseStatusException') && (errMsg || msg)) {
      const maybe = (errMsg || msg || '');
      if (isDefiniteLockedString(maybe)) {
        return errMsg || msg || 'Your account has been locked due to multiple incorrect postcode attempts. Email customer.support@iehub.co.uk to unlock.';
      }
    }
    return null;
  };

  const schema: ScreenSchema = useMemo(() => postCode as unknown as ScreenSchema, []);

  // Type guards to safely extract verification response
  type VerificationResponse = { postcode?: string; verifiedCustomer?: string; _elapsed?: string };
  const isVerificationResponse = (value: unknown): value is VerificationResponse => {
    if (typeof value !== 'object' || value === null) return false;
    const obj = value as Record<string, unknown>;
    return 'verifiedCustomer' in obj || 'postcode' in obj;
  };
  const extractVerificationData = (resp: unknown): VerificationResponse | null => {
    if (typeof resp === 'object' && resp !== null) {
      const maybeAxios = resp as Record<string, unknown>;
      if ('data' in maybeAxios) {
        const inner = (maybeAxios as { data?: unknown }).data;
        return isVerificationResponse(inner) ? inner : null;
      }
    }
    return isVerificationResponse(resp) ? resp : null;
  };

  // Extract number of attempts from a variety of API envelope shapes
  const extractNoOfAttempts = (payload: unknown): number | null => {
    const coerceNumber = (val: unknown): number | null => {
      if (typeof val === 'number' && Number.isFinite(val)) return val;
      if (typeof val === 'string' && val.trim() !== '' && !Number.isNaN(Number(val))) return Number(val);
      return null;
    };

    const tryFromObject = (obj: Record<string, unknown> | null | undefined): number | null => {
      if (!obj) return null;
      const direct = coerceNumber((obj as { noOfAttempts?: unknown }).noOfAttempts);
      if (direct !== null) return direct;
      const data = (obj as { data?: unknown }).data as unknown;
      if (Array.isArray(data) && data.length > 0) {
        // Prefer last record as per legacy behaviour
        const last = data[data.length - 1] as Record<string, unknown>;
        return coerceNumber((last as { noOfAttempts?: unknown }).noOfAttempts);
      }
      if (data && typeof data === 'object') {
        return coerceNumber((data as { noOfAttempts?: unknown }).noOfAttempts);
      }
      return null;
    };

    if (payload && typeof payload === 'object') {
      return tryFromObject(payload as Record<string, unknown>);
    }
    return null;
  };

  const handleSubmit = async (data: unknown) => {
    const payload = data as Record<string, unknown>;
    // Persist address verification form
    setFormData('address-verification', payload);

    try {
      setLoading('address-verify', true);
      // Call verification API
      const response = await OnboardingService.updateCustomerVerification(payload, { skipErrorCapture: false });
      // Detect locked-account payload even when returned with 200 OK
      const respData = (response as { data?: unknown })?.data ?? response;
      const lockedFromPayload = extractLockedMessage(respData);
      if (lockedFromPayload) {
        setLockedMessage(lockedFromPayload);
        await navigateToAuthLocked(lockedFromPayload);
        return;
      }
      const verification = extractVerificationData(response);

      if (verification?.verifiedCustomer === 'Y') {
        showAlert({
          severity: 'success',
          message: 'Address verified successfully',
          autoHide: true,
          duration: 1500
        });
        setIsBlocked(false);
        // Show loading transition screen and proceed
        setIsVerifying(true);
        return;
      }

      // Non-verified response: show incorrect postcode and do not proceed (no attempt counting)
      showAlert({
        severity: 'warning',
        message: 'Incorrect postcode. Please check and try again.',
        autoHide: true,
        duration: 3000
      });
      return;
    } catch (error) {
      console.error(error);
      // Extract error info and detect backend-locked state
      type ErrorInfo = {
        status?: number;
        code?: string;
        message?: string;
        errorTitle?: string;
        errorMessage?: string;
      };
      const getErrorInfo = (err: unknown): ErrorInfo => {
        const e = (err as { error?: unknown }).error as {
          response?: {
            status?: number;
            data?: {
              code?: string;
              errorCode?: string;
              message?: string;
              errorMessage?: string;
              errorTitle?: string;
              title?: string;
            } | string;
          };
          message?: string;
        };
        const info: ErrorInfo = {};
        if (typeof e?.response?.status === 'number') {
          info.status = e.response.status;
        }
        const data = e?.response?.data as Record<string, unknown> | string | undefined;
        if (data && typeof data === 'object') {
          const dataObj = data as Record<string, unknown>;
          const codeMaybe = (dataObj as { code?: unknown }).code;
          if (typeof codeMaybe === 'string') info.code = codeMaybe;
          const errorCodeMaybe = (dataObj as { errorCode?: unknown }).errorCode;
          if (typeof errorCodeMaybe === 'string') info.code = errorCodeMaybe;
          const messageMaybe = (dataObj as { message?: unknown }).message;
          if (typeof messageMaybe === 'string') info.message = messageMaybe;
          const errorMessageMaybe = (dataObj as { errorMessage?: unknown }).errorMessage;
          if (typeof errorMessageMaybe === 'string') info.errorMessage = errorMessageMaybe;
          const errorTitleMaybe = (dataObj as { errorTitle?: unknown }).errorTitle;
          if (typeof errorTitleMaybe === 'string') info.errorTitle = errorTitleMaybe;
        }
        if (!info.message && typeof e?.message === 'string') {
          info.message = e.message;
        }
        return info;
      };
      const info = getErrorInfo(error);

      // Robustly extract response.data from diverse error shapes
      const extractResponseDataFromError = (err: unknown): unknown => {
        if (!err || typeof err !== 'object') return undefined;
        const root = err as Record<string, unknown>;
        const tryFromResponse = (obj: unknown): unknown => {
          if (!obj || typeof obj !== 'object') return undefined;
          const r = obj as Record<string, unknown>;
          return 'data' in r ? (r as { data?: unknown }).data : undefined;
        };
        const direct = tryFromResponse(root.response);
        if (direct !== undefined) return direct;
        const nestedError = root.error as unknown;
        const nestedDirect = nestedError && typeof nestedError === 'object'
          ? tryFromResponse((nestedError as Record<string, unknown>).response)
          : undefined;
        if (nestedDirect !== undefined) return nestedDirect;
        if ('data' in root) return (root as { data?: unknown }).data;
        return err;
      };

      const combinedMsg = [info.message, info.errorMessage, info.code, info.errorTitle]
        .filter((v): v is string => typeof v === 'string')
        .join(' ')
        .toLowerCase();

      const lockedFromPayload = extractLockedMessage(extractResponseDataFromError(error));

      const lockedFromStatus = info.status === 423 || info.status === 403; // treat 423 Locked or 403 Forbidden as locked
      const lockedFromMessage = isDefiniteLockedString(combinedMsg);

      if (lockedFromStatus || lockedFromMessage || lockedFromPayload) {
        const msg = lockedFromPayload || info.errorMessage || info.message || 'Your account has been locked due to multiple incorrect postcode attempts. Email customer.support@iehub.co.uk to unlock.';
        setLockedMessage(msg);
        await navigateToAuthLocked(msg);
        return;
      }

      const isVerificationFailure = (
        combinedMsg.includes('postcode') ||
        info.status === 400 ||
        info.status === 422
      );

      if (isVerificationFailure) {
        showAlert({
          severity: 'warning',
          message: 'Incorrect postcode. Please check and try again.',
          autoHide: true,
          duration: 3000
        });
        return;
      }

      showAlert({
        severity: 'warning',
        message: 'Address verification failed. Please try again.',
        autoHide: true,
        duration: 3000
      });
    } finally {
      setLoading('address-verify', false);
    }
  };

  const handleVerificationComplete = useCallback(() => {
    setIsVerifying(false);
    // Mark verification as freshly completed (prevents short redirect loops if backend flag propagation is delayed)
    try {
      if (typeof user?.userId === 'number' && user.userId > 0) {
        localStorage.setItem(
          'addressVerificationCompleted',
          JSON.stringify({ userId: user.userId, timestamp: Date.now() })
        );
      }
    } catch {
      // ignore
    }
    // Mark address verification as completed in localStorage
    // This prevents re-routing to verify-address if user navigates back
    try {
      const storedLcode = localStorage.getItem('pendingLcode');
      if (storedLcode) {
        const parsed = JSON.parse(storedLcode) as { lcode: string; userId: number; timestamp: number };
        // Store completion flag tied to user and lcode
        localStorage.setItem('addressVerified', JSON.stringify({
          userId: parsed.userId,
          lcode: parsed.lcode,
          timestamp: Date.now()
        }));
        // Clear the pending lcode now that verification is complete
        localStorage.removeItem('pendingLcode');
        console.log('AddressVerification - Marked as complete for user:', parsed.userId);
      }
    } catch (e) {
      console.warn('Failed to store verification completion:', e);
    }
    // Auto-redirect back to RootRoute after verification completes.
    // RootRoute will decide the next destination (including creditor Open Banking-only lock).
    // Clear browser query params before navigating to avoid carrying over old params
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname + window.location.hash);
    }
    navigate('/', { replace: true });
  }, [navigate, user?.userId]);

  useEffect(() => {
    if (!isVerifying) return;
    const id = setTimeout(() => {
      handleVerificationComplete();
    }, 2000);
    return () => clearTimeout(id);
  }, [isVerifying, handleVerificationComplete]);

  // Removed success screen; we auto-redirect after verification

  // Mobile layout measurements similar to onboarding
  useEffect(() => {
    const updateHeights = () => {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 0);
      setFooterHeight(footerRef.current?.offsetHeight ?? 0);
    };
    updateHeights();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateHeights);
      if (headerRef.current) resizeObserver.observe(headerRef.current);
      if (footerRef.current) resizeObserver.observe(footerRef.current);
    }

    window.addEventListener('resize', updateHeights);
    return () => {
      window.removeEventListener('resize', updateHeights);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, []);

  // Track viewport for desktop vs mobile to decide dialog vs drawer presentation
  useEffect(() => {
    const updateIsDesktop = () => {
      if (typeof window !== 'undefined') {
        setIsDesktop(window.innerWidth >= 900);
      }
    };
    updateIsDesktop();
    window.addEventListener('resize', updateIsDesktop);
    return () => {
      window.removeEventListener('resize', updateIsDesktop);
    };
  }, []);

  // Guard: Honor API on mount to prevent incorrect entry into this screen
  useEffect(() => {
    (async () => {
      try {
        const stored = localStorage.getItem('customerData');
        const userId = stored ? (JSON.parse(stored) as { userId?: number }).userId : undefined;
        if (!userId) return;

        const { verifyCustomer, noOfAttempts } = await OnboardingService.isCustomerVerificationRequired(userId, { skipErrorCapture: true } as any);
        const verify = (verifyCustomer || '').toUpperCase();
        const attempts = Number(noOfAttempts ?? 0) || 0;

        if (verify === 'N') {
          // Clear browser query params before navigating to avoid carrying over old params
          if (window.location.search) {
            window.history.replaceState(null, '', window.location.pathname + window.location.hash);
          }
          const target = '/new-onboarding?step=0';
          navigate(target, { replace: true });
          return;
        }

        if (attempts >= 3) {
          await navigateToAuthLocked();
          return;
        }
      } catch (e) {
        // Non-blocking: if API fails, allow screen to render
        console.warn('AddressVerification - Guard check failed:', e);
      }
    })();
  }, [navigate]);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };
  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };
  const handleLogoutMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLogoutMenuAnchor(event.currentTarget);
  };
  const handleLogoutMenuClose = () => {
    setLogoutMenuAnchor(null);
  };
  const handleLogout = async () => {
    handleMobileMenuClose();
    try {
      await logout({ source: 'manual' });
    } finally {
      navigate('/auth', { replace: true });
    }
  };

  const navigateToAuthLocked = async (customMessage?: string): Promise<void> => {
    const description = (typeof customMessage === 'string' && customMessage.trim().length > 0)
      ? customMessage
      : (lockedMessage || 'Your account has been locked due to multiple incorrect postcode attempts. Email customer.support@iehub.co.uk to unlock.');
    
    // Logout user first (mark as account-locked to prevent global "session expired" alert)
    try {
      await logout({ source: 'account_locked' });
    } catch (error) {
      console.error('Logout failed during account lock:', error);
    }
    
    // Wait a bit for auth state to propagate (prevents race condition with GuestOnly guard)
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Clear local state
    setErrorSheetOpen(false);
    setIsBlocked(false);
    
    // Navigate to auth with error state to show dialog
    navigate('/auth', {
      replace: true,
      state: {
        magicLinkError: {
          message: 'Account Locked',
          description
        }
      }
    });
  };

  const handleLockedDismiss = async (): Promise<void> => {
    await navigateToAuthLocked();
  };

  const handleHelpOpen = () => openFaqForSchema({ helpTopicId: 'onboarding.general' });

  const handleBack = () => {
    navigate(-1);
  };

  // No full-page block; show slide-up bottom sheet instead (consistent with Auth)

  if (isVerifying) {
    return <SimpleLoadingScreen title="Address verified. Redirecting..." />;
  }

  // Success screen removed; we auto-redirect instead
  return (
    <>
    <SplitFormLayout
      images={carouselImages}
      contentMaxWidth={500}
      rightAltText="Onboarding"
      mobile={
        <Box data-testid="pages-private-on-boarding-address-verification-container-action-1" sx={{
          display: { xs: 'flex', md: 'none' },
          height: '100dvh',
          width: '100vw',
          backgroundColor: '#2C5F5D',
          flexDirection: 'column',
          position: 'relative'
        }}>
          {/* Fixed Mobile Header */}
          <Box data-testid="pages-private-on-boarding-address-verification-container-action-2" ref={headerRef} sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #e0e0e0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {/* Top row with Logo and Menu */}
            <Box data-testid="pages-private-on-boarding-address-verification-container-action-3" sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px',
              height: '56px'
            }}>
              <Box data-testid="pages-private-on-boarding-address-verification-container-action-4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box data-testid="pages-private-on-boarding-address-verification-container-action-5" component="img" src="/icons/logo.png" alt="IE Hub" sx={{ width: 86, height: 44, borderRadius: 1 }} />
              </Box>
              <IconButton data-testid="pages-private-on-boarding-address-verification-btn-handle-mobile-menu-open" onClick={handleMobileMenuOpen} sx={{ color: '#033F44', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                <MenuIcon />
              </IconButton>
              <Menu data-testid="pages-private-on-boarding-address-verification-menu-action-1"
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem data-testid="pages-private-on-boarding-address-verification-menu-item-handle-logout-1" onClick={handleLogout} sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.light', color: 'error.contrastText' } }}>
                  <LogoutIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
            {/* Help Row (no progress for single step) */}
            <Box data-testid="pages-private-on-boarding-address-verification-container-action-6" sx={{ 
              px: 2, 
              pb: 2,
              pt: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center'
            }}>
              <Box data-testid="pages-private-on-boarding-address-verification-container-help?"
                onClick={() => openFaqForSchema({ helpTopicId: 'onboarding.general' })}
                sx={{ 
                  color: 'text.secondary', 
                  textDecoration: 'underline', 
                  fontSize: '0.875rem', 
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                Help?
              </Box>
            </Box>
          </Box>
          {/* Form Content with padding for fixed header/footer */}
          <Box data-testid="pages-private-on-boarding-address-verification-container-action-7" sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            backgroundColor: '#fff',
            px: 2,
            pt: `calc(${headerHeight}px + 8px)`,
            pb: `calc(${footerHeight}px + env(safe-area-inset-bottom))`,
            overflow: 'auto'
          }}>
            <Box data-testid="pages-private-on-boarding-address-verification-container-action-8" sx={{ flex: 1 }}>
              <Screen
                schema={schema}
                hideHeader={true}
                hideActions={true}
                initialData={allFormData['address-verification'] || {}}
                onSubmit={handleSubmit}
                formId="address-verification-form-mobile"
              />
            </Box>
          </Box>
          {/* Bottom Navigation - Mobile (Fixed) */}
          <Box data-testid="pages-private-on-boarding-address-verification-container-action-9" ref={footerRef} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1100, backgroundColor: '#fff' }}>
            <Footer
              handleBack={handleBack}
              shouldApplyTopBorder={true}
              shouldBackDisabled={true}
              nextButtonText="Verify address"
              formId="address-verification-form-mobile"
              nextButtonLoading={Boolean(loading?.['address-verify'])}
            />
          </Box>
        </Box>
      }
      leftHeader={
        <Box data-testid="pages-private-on-boarding-address-verification-container-action-10" sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e0e0e0',
          borderRadius: '12px 12px 0 0'
        }}>
          <Box data-testid="pages-private-on-boarding-address-verification-container-action-11" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, minHeight: '64px' }}>
            <Box data-testid="pages-private-on-boarding-address-verification-container-action-12" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box data-testid="pages-private-on-boarding-address-verification-container-action-13" component="img" src="/icons/logo.png" alt="IE Hub" sx={{ width: 86, height: 44, borderRadius: 1 }} />
            </Box>
            <IconButton data-testid="pages-private-on-boarding-address-verification-btn-handle-logout-menu-open"
              onClick={handleLogoutMenuOpen}
              sx={{
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              <MenuIcon />
            </IconButton>
            <Menu data-testid="pages-private-on-boarding-address-verification-menu-action-2"
              anchorEl={logoutMenuAnchor}
              open={Boolean(logoutMenuAnchor)}
              onClose={handleLogoutMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem data-testid="pages-private-on-boarding-address-verification-menu-item-handle-logout-2" onClick={handleLogout} sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.light', color: 'error.contrastText' } }}>
                <LogoutIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
          <Box data-testid="pages-private-on-boarding-address-verification-container-action-14" sx={{ 
            px: 2, 
            pb: 2,
            pt: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}>
            <Box data-testid="pages-private-on-boarding-address-verification-container-handle-help-open"
              onClick={handleHelpOpen}
              sx={{ 
                color: 'text.secondary', 
                textDecoration: 'underline', 
                fontSize: '0.875rem', 
                cursor: 'pointer',
                '&:hover': { color: 'primary.main' }
              }}
            >
              Help?
            </Box>
          </Box>
        </Box>
      }
      leftFooter={
        <Box data-testid="pages-private-on-boarding-address-verification-container-action-15" sx={{ borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
          <Box data-testid="pages-private-on-boarding-address-verification-container-action-16" sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
            <Button data-testid="pages-private-on-boarding-address-verification-btn-verify-address" type="submit" variant="contained" color="primary" size="large" form="address-verification-form-desktop">
              Verify address
            </Button>
          </Box>
        </Box>
      }
    >
      {/* Desktop form content */}
      <Box data-testid="pages-private-on-boarding-address-verification-container-action-17" sx={{ flex: 1, px: 3, py: 1.5 }}>
        <Typography data-testid="pages-private-on-boarding-address-verification-text-verify-your-address" variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Verify your address
        </Typography>
        <Typography data-testid="pages-private-on-boarding-address-verification-text-enter-your-postcode-to-confirm-your-home-address." variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Enter your postcode to confirm your home address.
        </Typography>
        <Screen
          schema={schema}
          hideHeader={true}
          hideActions={true}
          initialData={allFormData['address-verification'] || {}}
          onSubmit={handleSubmit}
          formId="address-verification-form-desktop"
        />
      </Box>
    </SplitFormLayout>
    {/* Mobile error bottom sheet matching Auth screen */}
    <Drawer
      key="error-sheet"
      anchor="bottom"
      open={errorSheetOpen && isBlocked && !isDesktop}
      onClose={() => { void handleLockedDismiss(); }}
      sx={{
        '& .MuiDrawer-paper': {
          background: '#FFFFFF',
          borderRadius: '12px 12px 0px 0px',
          maxHeight: '85vh',
          minHeight: '65vh',
          height: 'auto',
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0
        }
      }}
    >
      <Box data-testid="pages-private-on-boarding-address-verification-container-action-18" sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Header with close */}
        <Box data-testid="pages-private-on-boarding-address-verification-container-action-19" sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderBottom: '1px solid #E5E7EB' }}>
          <IconButton data-testid="pages-private-on-boarding-address-verification-btn-action-1"
            onClick={() => { void handleLockedDismiss(); }}
            sx={{ p: 0.5, color: '#000000' }}
          >
            <CloseIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Box>
        {/* Content */}
        <Box data-testid="pages-private-on-boarding-address-verification-container-action-20" sx={{ px: 3, py: 5, textAlign: 'center' }}>
          <Box data-testid="pages-private-on-boarding-address-verification-container-action-21" sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box data-testid="pages-private-on-boarding-address-verification-container-action-22" component="img" src="/assets/onBoarding/postcodeBlocked.png" alt="Account Locked" sx={{ width: 300, maxWidth: '100%', height: 'auto' }} />
          </Box>
          <Typography data-testid="pages-private-on-boarding-address-verification-text-your-account-has-been-locked-1" sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: '22px', color: '#003F44', mb: 1.5 }}>
            Your Account Has Been Locked
          </Typography>
          <Typography data-testid="pages-private-on-boarding-address-verification-text-action-1" sx={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '14px', color: '#d32f2f', mb: 2.5 }}>
            { 'Postcode Verification Failed ' }
          </Typography>
          <Typography data-testid="pages-private-on-boarding-address-verification-text-action-2" sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '14px', color: 'rgba(49, 49, 49, 0.54)' , mb: 2.5}}>
          You entered the wrong postcode three times. Email at customer support@iehub.co.uk to unlock your Account.
          </Typography>
          <Typography data-testid="pages-private-on-boarding-address-verification-text-please-contact-support-for-help-1" 
            component="a"
            href="mailto:customer.support@iehub.co.uk"
            sx={{ 
              mb: 3.5,
              fontFamily: 'Roboto', 
              fontWeight: 400, 
              fontSize: '14px', 
              color: '#4A6B70',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: '#2d4a4e'
              }
            }}
          >
            Please Contact Support for help
          </Typography>
        </Box>
      </Box>
    </Drawer>
    {/* Desktop locked dialog */}
    <Dialog data-testid="pages-private-on-boarding-address-verification-modal-action"
      open={errorSheetOpen && isBlocked && isDesktop}
      onClose={() => { void handleLockedDismiss(); }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          width: '100%',
          maxWidth: 520
        }
      }}
   >
      <Box data-testid="pages-private-on-boarding-address-verification-container-action-23" sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderBottom: '1px solid #E5E7EB' }}>
        <IconButton data-testid="pages-private-on-boarding-address-verification-btn-action-2"
          onClick={() => { void handleLockedDismiss(); }}
          sx={{ p: 0.5, color: '#000000' }}
        >
          <CloseIcon sx={{ fontSize: '18px' }} />
        </IconButton>
      </Box>
      <Box data-testid="pages-private-on-boarding-address-verification-container-action-24" sx={{ px: 3, py: 5, textAlign: 'center' }}>
        <Box data-testid="pages-private-on-boarding-address-verification-container-action-25" sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box data-testid="pages-private-on-boarding-address-verification-container-action-26" component="img" src="/assets/onBoarding/postcodeBlocked.png" alt="Account Locked" sx={{ width: 180, maxWidth: '100%', height: 'auto' }} />
        </Box>
        <Typography data-testid="pages-private-on-boarding-address-verification-text-your-account-has-been-locked-2" sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: '22px', color: '#003F44', mb: 1.5 }}>
          Your Account Has Been Locked
        </Typography>
        <Typography data-testid="pages-private-on-boarding-address-verification-text-action-3" sx={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '14px', color: '#d32f2f', mb: 1.5 }}>
          { lockedMessage || 'Your account has been locked due to multiple incorrect postcode attempts. Email customer.support@iehub.co.uk to unlock.' }
        </Typography>
        <Typography data-testid="pages-private-on-boarding-address-verification-text-please-contact-support-for-help-2" 
          component="a"
          href="mailto:customer.support@iehub.co.uk"
          sx={{ 
            fontFamily: 'Roboto', 
            fontWeight: 400, 
            fontSize: '14px', 
            color: '#4A6B70',
            textDecoration: 'underline',
            cursor: 'pointer',
            '&:hover': {
              color: '#2d4a4e'
            }
          }}
        >
          Please Contact Support for help
        </Typography>
      </Box>
    </Dialog>
    </>
  );
}


